import { emptyTextNode } from '../../util/emptyTextNode';
import { Editor, Text, Element, type Descendant } from 'slate';
import addEmptyTextAtInlineEdges from './addEmptyTextAtInlineEdges';

const isBlock = (editor: Editor, value: Descendant) => {
  return Element.isElement(value) && Editor.isBlock(editor, value);
};

/**
 * We require all children of list items and table cells to be block
 * nodes, but TinyMCE (and some other editors) use raw text nodes
 * instead. So we'll look for all runs of non-block nodes, and wrap
 * them in `wrapperType` tags.
 */
export default function wrapNonBlocksInContainer<T extends Element>(
  editor: Editor,
  nodeList: Descendant[],
  wrapperType: T['type']
) {
  return nodeList.reduce<{ inlines: Descendant[]; nodes: Descendant[] }>(
    (acc, node, idx) => {
      let { inlines, nodes } = acc;
      let skip = false;

      // Strip empty text nodes from the beginning and end of the list.
      if ((idx === 0 || idx === nodeList.length - 1) && emptyTextNode(node)) {
        skip = true;
      }

      // Strip empty text nodes between block nodes.
      if (
        emptyTextNode(node) &&
        idx > 0 &&
        idx < nodeList.length - 1 &&
        isBlock(editor, nodeList[idx - 1]) &&
        isBlock(editor, nodeList[idx + 1])
      ) {
        skip = true;
      }

      // Collect the inline or text nodes.
      if (Text.isText(node) || Editor.isInline(editor, node)) {
        if (!skip) {
          inlines.push(node);
        }

        // If this is the end of the list wrap the children.
        if (idx === nodeList.length - 1 && inlines.length > 0) {
          nodes.push({
            type: wrapperType,
            children: addEmptyTextAtInlineEdges(editor, inlines),
          } as T);
        }
      } else {
        // If we have collected any inlines, wrap them and add to nodes.
        if (inlines.length > 0) {
          nodes.push({
            type: wrapperType,
            children: addEmptyTextAtInlineEdges(editor, inlines),
          } as T);
          inlines = [];
        }
        nodes.push(node);
      }

      return { inlines, nodes };
    },
    { inlines: [], nodes: [] }
  ).nodes;
}
